/**
 * AIChatSidePanel - Reusable AI chat component that can be embedded as a side panel
 * in any module to discuss entities (deals, items, candidates, etc.) with the AI.
 *
 * Supports two modes:
 * - Basic: Simple entity-linked chat (no MCP tools, no model selector)
 * - Enhanced: Full MCP tools, model selector, message merging with tool call display
 *   (activated by providing enabledModules prop)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  MessageSquare,
  Video,
  Plus,
  ChevronDown,
  Loader2,
  History,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '../../../components/ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { ChatBox } from './ChatBox';
import { AvatarCallView } from './AvatarCallView';
import type { ChatInputHandle } from './ChatInput';
import { ChatModelSelector } from './ChatModelSelector';
import type { ChatMessage, McpConfig } from './types';
import { mergeMessagesBySeq } from './chatMessageStore';
import { useChatSession } from '../../hooks/useChatSession';
import { useChatRouting } from '../../hooks/useChatRouting';
import { useChatSessionMachine } from '../../hooks/useChatSessionMachine';
import { usePendingMcpConfig } from '../../hooks/usePendingMcpConfig';
import { resolveScopeSyncPatch, toTurnMessageContext } from './mcpSessionConfig';
import { useOverlayBackDismiss } from '../../hooks/useOverlayBackDismiss';
import { useResponsive } from '../../hooks/useResponsive';
import { useRelativeDate } from '../../hooks';
import { useDirection } from '../../contexts/DirectionContext';
import { useCurrentAIModel, formatModelName } from '../../hooks/useCurrentAIModel';
import { chatApi, ChatSession, ChatSessionMessage } from '../../../modules/ai_chat/services/chatApi';
import {
  useChatSessionsByEntity,
  useChatSessionDetail,
  useCreateSession,
  useUpdateSession,
} from '../../../modules/ai_chat/hooks/useChat';
import { avatarSessionApi } from '../../../modules/avatars/services/avatarSessionApi';

export interface AIChatSidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Entity reference for context (e.g., "deals:uuid", "tasks:uuid") */
  entityRef: string;
  /** Display name for the entity (used in chat title) */
  entityName: string;
  /** Entity type label for UI (e.g., "Deal", "Task") - uses i18n key */
  entityTypeKey?: string;
  /** Custom title for the panel header */
  title?: string;
  /** Custom placeholder for the input */
  inputPlaceholder?: string;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Optional starter prompts shown as clickable suggestions in the empty state */
  suggestedPrompts?: string[];
  /** Width of the panel (default: 480px) */
  width?: string;
  /** Whether to auto-focus the input when panel opens */
  autoFocus?: boolean;
  /** Controlled mode: externally-managed chat session ID (e.g., from URL params) */
  chatSessionId?: string | null;
  /** Controlled mode: callback when active session changes (for URL sync) */
  onChatSessionChange?: (sessionId: string | null) => void;

  // Enhanced mode props (presence of enabledModules activates enhanced mode)

  /** MCP modules to enable for this chat (e.g., ['sales'], ['tasks']) */
  enabledModules?: string[];
  /** Module access levels (e.g., { sales: 'readwrite' }) */
  moduleAccess?: Record<string, 'read' | 'readwrite'>;
  /** Modules that cannot be disabled (e.g., { sales: 'readwrite' }) */
  lockedModules?: Record<string, 'read' | 'readwrite'>;
  /** Additional context keys passed to session (e.g., { deal_id: 'uuid' }) */
  context?: Record<string, unknown>;
  /** Tool scopes to filter MCP tools (e.g., { tasks: ['task_editing'] }) */
  toolScopes?: Record<string, string[]>;
  /** Called when AI finishes generating a response (for query invalidation) */
  onGenerationComplete?: () => void;
  /** Show entity name badge below header */
  showEntityBadge?: boolean;
  /** Extra content rendered below the header (e.g., mode toggle) */
  headerExtra?: React.ReactNode;
  /**
   * Override the attached entities for the next created session. When set,
   * takes precedence over the single `entityRef`. Used by the chat launcher
   * to attach multiple entities (e.g., bulk task selection → "Ask Luma").
   * Chips are rendered above the input; each can be removed before sending.
   */
  aboutEntitiesOverride?: Array<{ ref: string; label: string; type?: string }>;
  /** Called when the user removes a chip pre-send (override-mode only). */
  onAboutEntitiesOverrideChange?: (
    next: Array<{ ref: string; label: string; type?: string }>,
  ) => void;
  /**
   * Pre-fill the input with this text for a launched session (editable, not
   * auto-sent). Held internally so it survives the input remount during session
   * connect, and cleared once the first message is sent.
   */
  initialInput?: string;
}

interface SessionHistoryItem {
  id: string;
  title: string;
  messageCount: number;
  updatedAt: string;
}

export function AIChatSidePanel({
  open,
  onClose,
  entityRef,
  entityName,
  entityTypeKey = 'common:entities.entity',
  title,
  inputPlaceholder,
  emptyMessage,
  suggestedPrompts,
  width = 'w-full sm:w-[480px]',
  autoFocus = true,
  chatSessionId,
  onChatSessionChange,
  enabledModules,
  moduleAccess,
  lockedModules,
  context: contextProp,
  toolScopes,
  onGenerationComplete,
  showEntityBadge,
  headerExtra,
  aboutEntitiesOverride,
  onAboutEntitiesOverrideChange,
  initialInput,
}: AIChatSidePanelProps) {
  const { t } = useTranslation(['aiChat', 'common']);
  const formatRelative = useRelativeDate();

  const isEnhancedMode = !!enabledModules && enabledModules.length > 0;

  // ── Voice/avatar call mode ──
  // Availability gates the header call button: a deployment without LiveKit
  // credentials never shows a dead affordance. Config presence changes only on
  // redeploy, so cache it long.
  const queryClient = useQueryClient();
  const { data: avatarAvailability } = useQuery({
    queryKey: ['avatars', 'livekit-availability'],
    queryFn: avatarSessionApi.getAvailability,
    enabled: open,
    staleTime: 30 * 60 * 1000,
  });
  const [callActive, setCallActive] = useState(false);

  const { isMobile } = useResponsive();
  // In RTL languages (e.g. Hebrew) the panel slides in from the left so it sits
  // on the same side as the rest of the RTL UI; LTR keeps it on the right.
  const { isRtl } = useDirection();
  const panelSide = isRtl ? 'left' : 'right';
  // Mobile: the panel is a full-screen overlay, so Back should collapse it
  // instead of navigating away from the underlying page. onClose keeps the
  // session bound — callers (GlobalAIButton, entity panels) keep this component
  // mounted across open/close, so collapsing never unmounts useChatSession.
  useOverlayBackDismiss({ open, enabled: isMobile, onDismiss: onClose });

  // The single source of truth for the active session id. `useChatRouting` owns
  // it (synchronous setter) and, in controlled mode, mirrors it to/from the
  // external URL param the parent manages (GlobalAIButton's `?luma=`, page
  // panels' `?chat=`). Passing `chatSessionId` straight through doubles as the
  // controlled/uncontrolled discriminator: `undefined` → uncontrolled.
  const { sessionId: activeSessionId, setSessionId: setActiveSessionId } = useChatRouting({
    externalSessionId: chatSessionId,
    onExternalSessionChange: onChatSessionChange,
    // Drop the bound session when the panel re-targets a new entity — but ONLY
    // in uncontrolled mode. In controlled mode the URL owner decides: the global
    // Luma panel (`?luma=`) keeps its session across page navigation even though
    // its entityRef changes, so resetting on entityRef there would wrongly drop
    // the live global session on every navigation.
    resetKey: chatSessionId === undefined ? entityRef : undefined,
  });

  // ── Basic mode: direct API session management ──
  const [basicEntitySessions, setBasicEntitySessions] = useState<SessionHistoryItem[]>([]);
  const [isLoadingBasicSessions, setIsLoadingBasicSessions] = useState(false);
  // True once the basic-mode session list has been fetched at least once for the
  // current entity/open. Gates the machine's auto-select so it doesn't bind to
  // "no session" before the list has loaded. (Enhanced mode uses the query's
  // `isFetched`.)
  const [basicSessionsLoaded, setBasicSessionsLoaded] = useState(false);

  // ── Enhanced mode: React Query session management ──
  const {
    data: enhancedSessionsData,
    isLoading: isLoadingEnhancedSessions,
    isFetched: isFetchedEnhancedSessions,
  } = useChatSessionsByEntity(isEnhancedMode && open ? entityRef : null);
  const { data: sessionDetail } = useChatSessionDetail(
    isEnhancedMode ? activeSessionId : null,
  );
  const createSessionMutation = useCreateSession();
  const updateSessionMutation = useUpdateSession();
  const { modelName: globalModelName, allowModelChoice } = useCurrentAIModel();
  // Pre-session model category: buffered locally until the first message creates
  // a session, then applied at creation time. Lets the user pick a model before
  // sending anything in a brand-new chat. The ref mirrors the state so the
  // session-create path in handleSendMessage reads the latest value without a
  // stale closure (the create payload is built synchronously on send).
  const [pendingModelCategory, setPendingModelCategory] = useState<string | null>(null);
  const pendingModelCategoryRef = useRef<string | null>(null);
  // Pre-session MCP config: same buffer pattern as the model category above, so
  // the tool-config picker is usable before the first message. Seeded from the
  // embedder's enabledModules/moduleAccess props and folded into the
  // create-session payload on first send. `resetKey` drops a pick made for a
  // scope the embedder has since navigated away from.
  const embedderScopeKey = JSON.stringify([enabledModules ?? [], moduleAccess ?? {}]);
  const {
    pendingConfig: pendingMcpConfig,
    setPendingConfig: setPendingMcpConfig,
    clearPendingConfig: clearPendingMcpConfig,
    toCreateContext: pendingMcpConfigToContext,
  } = usePendingMcpConfig({ resetKey: embedderScopeKey });
  const [pendingReasoningEffort, setPendingReasoningEffort] = useState<string | null>(null);
  const pendingReasoningEffortRef = useRef<string | null>(null);
  const sessionModelCategory = activeSessionId
    ? (sessionDetail?.data?.model_category ?? null)
    : pendingModelCategory;
  // Effort is chosen the same way and buffered the same way as the model: it
  // can be set before the first message exists, so it rides along in the
  // create-session payload rather than needing a session to patch.
  const sessionReasoningEffort = activeSessionId
    ? (sessionDetail?.data?.reasoning_effort ?? null)
    : pendingReasoningEffort;
  // Use session's effective model when available (e.g., agent-specific model), fall back to global
  const sessionEffectiveModelId = sessionDetail?.data?.effective_model_id ?? null;
  const modelName = sessionEffectiveModelId
    ? formatModelName(sessionEffectiveModelId)
    : globalModelName;

  // Build chat context for enhanced mode
  const chatContext = useMemo(() => {
    if (!isEnhancedMode) return undefined;
    return {
      enabled_modules: enabledModules,
      module_access: moduleAccess,
      ...(toolScopes ? { tool_scopes: toolScopes } : {}),
      ...contextProp,
    };
  }, [isEnhancedMode, enabledModules, moduleAccess, toolScopes, contextProp]);

  // Chat session hook
  const {
    session,
    streaming,
    createSession,
    loadSession,
    sendMessage,
    stopGeneration,
    reset,
  } = useChatSession({
    chatType: 'assistant',
    sessionId: activeSessionId || undefined,
    ...(isEnhancedMode ? { context: chatContext, aboutEntities: [entityRef] } : {}),
  });

  // ── Unified session list ──
  const enhancedEntitySessions = useMemo<SessionHistoryItem[]>(() => {
    if (!isEnhancedMode) return [];
    const sessions = enhancedSessionsData?.data ?? [];
    return sessions
      .filter((s: ChatSession) => s.status !== 'archived')
      .map((s: ChatSession) => ({
        id: s.id,
        title: s.title || t('aiChat:untitledChat'),
        messageCount: s.message_count,
        updatedAt: s.updated_at || s.created_at,
      }));
  }, [isEnhancedMode, enhancedSessionsData, t]);

  const entitySessions = isEnhancedMode ? enhancedEntitySessions : basicEntitySessions;
  const isLoadingSessions = isEnhancedMode ? isLoadingEnhancedSessions : isLoadingBasicSessions;
  // The session list has settled (loaded or errored) for this open — the
  // machine's auto-select transition waits on this.
  const sessionsReady = isEnhancedMode ? isFetchedEnhancedSessions : basicSessionsLoaded;

  // Clear the buffered pre-session choices (model + MCP config) — shared by New
  // Chat and session switch. Both buffers only ever describe a not-yet-created
  // session, so they must not survive either transition.
  const clearPendingTransient = useCallback(() => {
    setPendingModelCategory(null);
    pendingModelCategoryRef.current = null;
    clearPendingMcpConfig();
  }, [clearPendingMcpConfig]);

  // ── Explicit session lifecycle state machine ──
  // Owns the phase (idle/selecting/bound/streaming/interrupting) and the
  // transitions for auto-select, New Chat, switch, and Stop. Builds on
  // useChatRouting (which stays the single owner of the id) and bridges the
  // authoritative streaming flag for turn start/finish. Replaces the old
  // hasAutoSelectedRef auto-select effect and the ad-hoc reset() dance.
  const {
    newChat: machineNewChat,
    switchSession: machineSwitchSession,
    stop: machineStop,
    reselect: machineReselect,
  } = useChatSessionMachine({
    open,
    sessionId: activeSessionId,
    setSessionId: setActiveSessionId,
    // StreamingState.isGenerating is optional at the type level; useChatSession
    // always sets it, so coerce the undefined-typed case to false.
    isGenerating: streaming.isGenerating ?? false,
    resetSession: reset,
    stopGeneration,
    entitySessions,
    sessionsReady,
    onResetTransient: clearPendingTransient,
  });

  // Re-target the panel when entityRef changes (e.g. toggling module/global
  // scope, or navigating between entities). Always drops the basic-mode session
  // list so it reloads for the new entity. In UNCONTROLLED mode it also re-enters
  // selection so auto-select picks the new entity's most-recent session (the id
  // itself is cleared by useChatRouting's resetKey). Controlled mode (the global
  // Luma `?luma=` panel) keeps its session across navigation and must NOT
  // reselect — mirroring useChatRouting's resetKey gating.
  const isUncontrolled = chatSessionId === undefined;
  const prevEntityRef = useRef(entityRef);
  useEffect(() => {
    if (prevEntityRef.current === entityRef) return;
    prevEntityRef.current = entityRef;
    setBasicEntitySessions([]);
    setBasicSessionsLoaded(false);
    if (isUncontrolled && open) {
      machineReselect();
    }
  }, [entityRef, isUncontrolled, open, machineReselect]);

  // ── Enhanced mode: MCP config ──
  const sessionEnabledModules = useMemo(
    () =>
      sessionDetail?.data?.enabled_modules || session.enabledModules || [],
    [sessionDetail?.data?.enabled_modules, session.enabledModules],
  );

  // The primary module and every locked module stay enabled no matter what the
  // picker returns — the embedding page's own tools are not the user's to remove.
  const ensureLockedModules = useCallback(
    (config: McpConfig): McpConfig => {
      const primaryModule = enabledModules?.[0];
      const ensuredModules =
        primaryModule && !config.enabledModules.includes(primaryModule)
          ? [...config.enabledModules, primaryModule]
          : config.enabledModules;
      const mergedAccess = { ...config.moduleAccess, ...(lockedModules || {}) };
      return {
        ...config,
        enabledModules: ensuredModules,
        ...(Object.keys(mergedAccess).length > 0 ? { moduleAccess: mergedAccess } : {}),
      };
    },
    [enabledModules, lockedModules],
  );

  const mcpConfig: McpConfig | undefined = useMemo(() => {
    if (!isEnhancedMode) return undefined;
    if (!activeSessionId) {
      // Pre-session: the buffered choice, falling back to the embedder's defaults.
      const base = pendingMcpConfig ?? {
        enabledModules: enabledModules ?? [],
        moduleAccess,
      };
      return { ...ensureLockedModules(base), lockedModules };
    }
    const ctx = sessionDetail?.data?.context as Record<string, unknown> | undefined;
    return {
      enabledModules: sessionEnabledModules,
      moduleAccess: ctx?.module_access as Record<string, 'read' | 'readwrite'> | undefined,
      enabledTools: ctx?.enabled_tools as string[] | undefined,
      disabledTools: ctx?.disabled_tools as string[] | undefined,
      webSearchEnabled: ctx?.web_search !== false,
      webFetchEnabled: ctx?.web_fetch !== false,
      webNavigateEnabled: ctx?.web_navigate === true,
      lockedModules,
    };
  }, [
    isEnhancedMode,
    activeSessionId,
    pendingMcpConfig,
    enabledModules,
    moduleAccess,
    ensureLockedModules,
    sessionEnabledModules,
    sessionDetail?.data?.context,
    lockedModules,
  ]);

  // ── Embedder scope → the bound session, explicitly ──
  // The session's persisted context is the ONE source of truth for a turn's
  // tools (the turn itself carries none — see handleSendMessage). So an
  // embedder that re-scopes a BOUND session has to SAY so: the global Luma
  // panel keeps one session across page navigation while its enabledModules
  // prop follows the page, and without this PATCH that session would stay
  // stranded on the module scope it was opened with.
  //
  // Union semantics: add the page's modules, never remove or re-level one the
  // session already carries (resolveScopeSyncPatch owns the rule). The user's
  // own picker choices therefore survive navigation, and the write is visible
  // in the picker and survives a reload — unlike the invisible per-turn prop
  // override it replaces.
  //
  // Two guards, both about reading the RIGHT session:
  //  - `sessionDetail.data.id === activeSessionId` — loadSession unblocks the
  //    input on its own fetch, independently of this React Query request, so a
  //    switch can leave the previous session's row in hand. Patching off that
  //    row would write one session's scope onto another.
  //  - an attempted-key ref, so a failed PATCH is not retried on every render.
  //    It caches nothing the SEND path reads.
  const scopeSyncAttemptedRef = useRef<string | null>(null);
  const loadedSessionDetail =
    sessionDetail?.data?.id === activeSessionId ? sessionDetail.data : undefined;
  useEffect(() => {
    if (!isEnhancedMode || !activeSessionId || !loadedSessionDetail) return;
    const attemptKey = `${activeSessionId}|${embedderScopeKey}|${JSON.stringify(toolScopes ?? null)}`;
    if (scopeSyncAttemptedRef.current === attemptKey) return;
    const patch = resolveScopeSyncPatch({
      persistedModules: loadedSessionDetail.enabled_modules ?? [],
      persistedContext: loadedSessionDetail.context as Record<string, unknown> | undefined,
      embedderModules: enabledModules ?? [],
      embedderAccess: moduleAccess,
      embedderToolScopes: toolScopes,
    });
    if (!patch) return;
    scopeSyncAttemptedRef.current = attemptKey;
    updateSessionMutation.mutateAsync({ sessionId: activeSessionId, data: patch }).catch(() => {
      // Error surfaced by the mutation's global toast; the chat keeps the scope
      // it already had rather than silently pretending to have widened.
    });
    // updateSessionMutation is a new object identity on every render, so it is
    // deliberately not a dependency — the attempted-key ref is the run-once
    // guard, not the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEnhancedMode,
    activeSessionId,
    loadedSessionDetail,
    embedderScopeKey,
    enabledModules,
    moduleAccess,
    toolScopes,
  ]);

  // ── Enhanced mode: seq-keyed history projection ──
  const historyMessages: ChatMessage[] = useMemo(() => {
    if (!isEnhancedMode || !sessionDetail?.data?.messages) return [];
    return sessionDetail.data.messages.map((msg: ChatSessionMessage, idx: number) => ({
      id: msg.id || `history-${idx}`,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      ...(typeof msg.seq === 'number' ? { seq: msg.seq } : {}),
      ...(msg.tool_calls ? { tool_calls: msg.tool_calls as ChatMessage['tool_calls'] } : {}),
      ...(msg.attachment_file_ids?.length ? { attachment_file_ids: msg.attachment_file_ids } : {}),
      ...(msg.is_compaction_summary ? { is_compaction_summary: true } : {}),
      ...(msg.interrupted ? { interrupted: true } : {}),
    }));
  }, [isEnhancedMode, sessionDetail?.data?.messages]);

  // Single seq-keyed projection: REST history + live (WS/optimistic) messages
  // merged and de-duplicated by their canonical `seq`. No prefer-live /
  // fallback-history branch — the old approach dropped one source entirely
  // because id-based merging duplicated when ids didn't align. See
  // chatMessageStore.mergeMessagesBySeq and the A3 design note.
  const displayMessages: ChatMessage[] = useMemo(() => {
    if (!isEnhancedMode) return session.messages;
    return mergeMessagesBySeq(historyMessages, session.messages);
  }, [isEnhancedMode, historyMessages, session.messages]);

  // ── Enhanced mode: MCP config change handler ──
  const handleMcpConfigChange = useCallback(
    async (newConfig: McpConfig) => {
      if (!isEnhancedMode || !enabledModules) return;
      const ensured = ensureLockedModules(newConfig);
      if (!activeSessionId) {
        // No session yet — buffer the choice; folded into the create payload
        // when the user's first message creates the session.
        setPendingMcpConfig(ensured);
        return;
      }
      try {
        await updateSessionMutation.mutateAsync({
          sessionId: activeSessionId,
          data: {
            enabled_modules: ensured.enabledModules,
            module_access: ensured.moduleAccess,
            enabled_tools: ensured.enabledTools,
            disabled_tools: ensured.disabledTools,
            web_search: ensured.webSearchEnabled,
            web_fetch: ensured.webFetchEnabled,
            web_navigate: ensured.webNavigateEnabled,
          },
        });
      } catch {
        // Error handled by mutation
      }
    },
    [
      activeSessionId,
      isEnhancedMode,
      enabledModules,
      ensureLockedModules,
      updateSessionMutation,
      setPendingMcpConfig,
    ],
  );

  // ── Enhanced mode: model change handler ──
  const handleModelChange = useCallback(
    async (categoryId: string | null) => {
      if (!categoryId) return;
      if (!activeSessionId) {
        // No session yet — buffer the choice; applied when the session is
        // created on the user's first message.
        setPendingModelCategory(categoryId);
        pendingModelCategoryRef.current = categoryId;
        return;
      }
      try {
        await updateSessionMutation.mutateAsync({
          sessionId: activeSessionId,
          data: { model_category: categoryId },
        });
      } catch {
        // Error handled by mutation
      }
    },
    [activeSessionId, updateSessionMutation],
  );

  const handleReasoningEffortChange = useCallback(
    async (level: string | null) => {
      if (!activeSessionId) {
        setPendingReasoningEffort(level);
        pendingReasoningEffortRef.current = level;
        return;
      }
      try {
        // null is sent explicitly, not omitted: the endpoint distinguishes an
        // absent field (no change) from an explicit null (clear the override).
        await updateSessionMutation.mutateAsync({
          sessionId: activeSessionId,
          data: { reasoning_effort: level },
        });
      } catch {
        // Error handled by mutation
      }
    },
    [activeSessionId, updateSessionMutation],
  );

  // ── Generation complete callback ──
  const prevIsGenerating = useRef(streaming.isGenerating);
  useEffect(() => {
    if (prevIsGenerating.current && !streaming.isGenerating) {
      onGenerationComplete?.();
    }
    prevIsGenerating.current = streaming.isGenerating;
  }, [streaming.isGenerating, onGenerationComplete]);

  // Default title based on entity type
  const panelTitle = title || t('aiChat:discussWith', {
    entity: t(entityTypeKey),
    defaultValue: `Discuss ${entityName}`,
  });

  // ── Basic mode: fetch sessions via direct API ──
  const fetchBasicEntitySessions = useCallback(async () => {
    if (isEnhancedMode) return;
    setIsLoadingBasicSessions(true);
    try {
      const result = await chatApi.getSessionsByEntity(entityRef);
      const sessions: SessionHistoryItem[] = result.data
        .filter((s: ChatSession) => s.status !== 'archived')
        .map((s: ChatSession) => ({
          id: s.id,
          title: s.title || t('aiChat:untitledChat'),
          messageCount: s.message_count,
          updatedAt: s.updated_at || s.created_at,
        }));
      setBasicEntitySessions(sessions);
      // Auto-select is owned by the state machine (selecting → bound); the list
      // just needs to be marked ready so it can run.
    } catch (error) {
      console.error('Failed to fetch entity sessions:', error);
    } finally {
      setIsLoadingBasicSessions(false);
      setBasicSessionsLoaded(true);
    }
  }, [isEnhancedMode, entityRef, t]);

  // Fetch sessions when panel opens (basic mode)
  useEffect(() => {
    if (open && !isEnhancedMode) {
      fetchBasicEntitySessions();
    }
  }, [open, isEnhancedMode, fetchBasicEntitySessions]);

  // Auto-select most recent session is now a state-machine transition
  // (selecting → bound), not a guarded effect — see useChatSessionMachine. The
  // phase itself is the run-once guard, so no hasAutoSelectedRef is needed.

  // Load session when activeSessionId changes to an EXISTING session that the
  // live hook hasn't bound yet (deep link, history switch, auto-select). The
  // `session.sessionId !== activeSessionId` guard is sufficient on its own now
  // that setActiveSessionId is synchronous: for a freshly-created session,
  // createSession()/sendMessage() set session.sessionId to the new id before
  // activeSessionId flips to it, so this effect either skips (activeSessionId
  // still null) or sees them already equal — never a transient mismatch to
  // re-load. No skip-next-load guard needed. (The old URL "heal" effect is gone
  // too: useChatRouting is the single owner and adopts genuine external URL
  // changes directly instead of healing them back.)
  useEffect(() => {
    if (activeSessionId && open && session.sessionId !== activeSessionId) {
      loadSession(activeSessionId);
    }
  }, [activeSessionId, loadSession, open, session.sessionId]);

  // Handle creating a new chat session.
  // We deliberately do NOT hit the backend here — handleSendMessage already
  // creates the session lazily on the user's first message. Eager creation
  // caused a visible "connecting" spinner and let the auto-select effect
  // flicker the history dropdown between IDs during the network round-trip.
  const chatInputRef = useRef<ChatInputHandle>(null);
  // Flag: after New Chat / switching session via the history dropdown, move
  // focus to the input instead of letting Radix restore it to the trigger.
  const focusInputOnDropdownCloseRef = useRef(false);

  // Launcher pre-fill text, held locally so it survives the ChatBox remount
  // that happens while the session connects (the input would otherwise be
  // recreated empty). Synced from the `initialInput` prop on each new launch
  // and cleared once the first message is sent.
  const [pendingInitialInput, setPendingInitialInput] = useState<string | undefined>(initialInput);
  useEffect(() => {
    setPendingInitialInput(initialInput);
  }, [initialInput]);

  const handleNewChat = useCallback(() => {
    machineNewChat();
    focusInputOnDropdownCloseRef.current = true;
  }, [machineNewChat]);

  // Handle switching to a different session
  const handleSwitchSession = useCallback((sessionId: string) => {
    machineSwitchSession(sessionId);
    focusInputOnDropdownCloseRef.current = true;
  }, [machineSwitchSession]);

  const handleDropdownCloseAutoFocus = useCallback((e: Event) => {
    if (focusInputOnDropdownCloseRef.current) {
      focusInputOnDropdownCloseRef.current = false;
      e.preventDefault();
      chatInputRef.current?.focus();
    }
  }, []);

  // Resolve the bound chat session, creating it lazily when none exists yet —
  // the shared front half of sending a message AND starting a voice call (the
  // avatar session must bind to a real chat_id so the worker can mirror the
  // panel's config and flush the call transcript back into it).
  // The create folds the buffered picker choice into the session's PERSISTED
  // context, which is what every later turn reads — the creating turn carries
  // no copy of it. Enhanced-mode create errors propagate to the caller (the
  // mutation's global toast already surfaced them).
  const ensureSession = useCallback(async (): Promise<string | null> => {
    // Prefer the live hook's id, but fall back to the panel's own activeSessionId:
    // after a create (or an auto-select) the panel knows the id synchronously
    // while `session.sessionId` only catches up once the loadSession effect
    // binds it. Reading the hook alone would create a SECOND chat for a
    // conversation that already exists.
    let resolvedSessionId = session.sessionId || activeSessionId;

    if (!resolvedSessionId) {
      const effectiveAboutEntities =
        aboutEntitiesOverride && aboutEntitiesOverride.length > 0
          ? aboutEntitiesOverride.map((e) => e.ref)
          : [entityRef];
      if (isEnhancedMode) {
        const result = await createSessionMutation.mutateAsync({
          chat_type: 'assistant',
          context: {
            ...(chatContext || {}),
            ...pendingMcpConfigToContext(),
          },
          about_entities: effectiveAboutEntities,
          title: entityName,
          ...(pendingModelCategoryRef.current
            ? { model_category: pendingModelCategoryRef.current }
            : {}),
          ...(pendingReasoningEffortRef.current
            ? { reasoning_effort: pendingReasoningEffortRef.current }
            : {}),
        });
        resolvedSessionId = result.data.id;
        setPendingModelCategory(null);
        pendingModelCategoryRef.current = null;
        setPendingReasoningEffort(null);
        pendingReasoningEffortRef.current = null;
        clearPendingMcpConfig();
        // setActiveSessionId is synchronous and sendMessage (in the send path)
        // sets session.sessionId to this same id before its first await, both in
        // this post-create continuation. The loadSession effect therefore
        // never sees activeSessionId=newId while session.sessionId still lags,
        // so it no-ops and won't race the WS connect — no skip guard needed.
        setActiveSessionId(resolvedSessionId);
      } else {
        // createSession() already connects WebSocket internally
        const sessionId = await createSession({
          chatType: 'assistant',
          context: {
            about_entities: effectiveAboutEntities,
          },
        });
        if (sessionId) {
          resolvedSessionId = sessionId;
          // createSession() already set session.sessionId + connected the WS,
          // so by the time the synchronous setActiveSessionId flips
          // activeSessionId to this id they already match — the loadSession
          // effect no-ops (and while activeSessionId was still null it was
          // skipped). No skip-next-load guard needed.
          setActiveSessionId(sessionId);
          setBasicEntitySessions(prev => [{
            id: sessionId,
            title: entityName,
            messageCount: 0,
            updatedAt: new Date().toISOString(),
          }, ...prev]);
        }
      }
    }

    return resolvedSessionId;
  }, [session.sessionId, activeSessionId, isEnhancedMode, createSessionMutation, chatContext, pendingMcpConfigToContext, clearPendingMcpConfig, entityRef, entityName, aboutEntitiesOverride, createSession, setActiveSessionId]);

  // Handle sending a message
  const handleSendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;
    // The pre-fill has served its purpose; clear it so a remount during session
    // connect doesn't re-seed the (now sent) text.
    setPendingInitialInput(undefined);

    // Track the session ID to pass directly to sendMessage,
    // bypassing stale closure state after session creation.
    // Don't call loadSession here — the useEffect on activeSessionId
    // handles WebSocket connection. Calling it here races with the
    // useEffect and causes "WebSocket closed before connection established".
    let resolvedSessionId: string | null;
    try {
      resolvedSessionId = await ensureSession();
    } catch {
      return; // create error already surfaced by the mutation's global toast
    }

    if (!resolvedSessionId) return;
    // Pass resolvedSessionId directly to avoid stale closure race condition.
    // Enhanced mode: first message uses SSE fallback (WS not yet connected).
    // Basic mode: createSession already established WS, so it may use WS.
    //
    // The turn carries the embedder's live PAGE/ENTITY context — that is why it
    // carries a context at all: module providers need fresh entity ids on every
    // message, not the ones the session was created with. It carries NOTHING
    // about tools: the backend merges message_context OVER the persisted
    // session context, so a prop-derived `enabled_modules` there would discard
    // the user's picker choice on every turn (toTurnMessageContext strips them,
    // and the scope-sync effect above is how an embedder re-scopes a session
    // instead). No ref caches the effective config here — a send that lands
    // mid-switch must never carry the previous session's anything.
    await sendMessage(text, toTurnMessageContext(chatContext), undefined, resolvedSessionId);
  }, [ensureSession, chatContext, sendMessage]);

  // ── Voice/avatar call plumbing ──
  // Mint the LiveKit avatar session for the call view: ensure the chat session
  // exists (creating it exactly like a first message would), then bind the
  // avatar session to that chat_id — the worker mirrors the chat's persona +
  // tools and flushes the call transcript back into it at end of call.
  // One in-flight mint per activation. The call view's start effect is
  // re-runnable (StrictMode double-invokes it in dev), and each mint spins a
  // real LiveKit room + agent dispatch — so the repeat must reuse the first
  // promise rather than buy a second room. Cleared when the call ends, and on
  // failure so a retry isn't served the cached rejection.
  const callSessionRef = useRef<Promise<{ livekit_url: string; token: string }> | null>(null);

  const createAvatarCallSession = useCallback(() => {
    if (!callSessionRef.current) {
      callSessionRef.current = (async () => {
        // Errors from ensureSession's create mutation already surfaced via the
        // global MutationCache toast; the call view renders the failure inline
        // (the hook is configured not to toast again).
        const sessionId = await ensureSession();
        if (!sessionId) {
          throw new Error('chat session unavailable');
        }
        const resp = await avatarSessionApi.createSession({ chat_id: sessionId });
        return { livekit_url: resp.livekit_url, token: resp.token };
      })().catch((err) => {
        callSessionRef.current = null;
        throw err;
      });
    }
    return callSessionRef.current;
  }, [ensureSession]);

  const handleCallEnd = useCallback(() => {
    setCallActive(false);
    callSessionRef.current = null;
    // The worker flushes the call transcript into the chat at job shutdown,
    // which trails the room disconnect by a few seconds — refetch now (covers
    // an already-flushed short call) and once more after the flush window so
    // the spoken turns appear as text without a manual reload.
    const refetch = () => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      if (!isEnhancedMode && activeSessionId) {
        loadSession(activeSessionId);
      }
    };
    refetch();
    window.setTimeout(refetch, 5000);
  }, [queryClient, isEnhancedMode, activeSessionId, loadSession]);

  // Closing the panel ends the call: a live mic behind a hidden panel is a
  // privacy hazard. Unmounting AvatarCallView tears the room down cleanly.
  useEffect(() => {
    if (!open && callActive) {
      setCallActive(false);
    }
  }, [open, callActive]);

  // Format session for dropdown display
  const formatSessionTime = useCallback((dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return '';
      }
      return formatRelative(date);
    } catch {
      return '';
    }
  }, [formatRelative]);

  // Active session display
  const activeSessionDisplay = useMemo(() => {
    if (activeSessionId) {
      const activeSession = entitySessions.find(s => s.id === activeSessionId);
      if (activeSession) {
        return activeSession.title;
      }
    }
    return t('aiChat:newChat');
  }, [activeSessionId, entitySessions, t]);

  // Default messages for empty state
  const defaultEmptyMessage = emptyMessage || t('aiChat:startConversation');

  // Suggested prompts: render only when the chat is brand-new (no messages yet,
  // no active session). Clicking a prompt sends it as the first message.
  const suggestedPromptsContent = useMemo(() => {
    if (!suggestedPrompts || suggestedPrompts.length === 0) return undefined;
    if (activeSessionId) return undefined;
    return (
      <div className="flex flex-col items-stretch gap-3 px-6 py-8 text-center">
        <p className="text-sm text-muted-foreground">{defaultEmptyMessage}</p>
        <div className="flex flex-col gap-2">
          {suggestedPrompts.map((prompt) => (
            <Button
              key={prompt}
              variant="outline"
              size="sm"
              className="h-auto whitespace-normal py-2 text-left text-sm font-normal justify-start"
              onClick={() => handleSendMessage(prompt)}
            >
              {prompt}
            </Button>
          ))}
        </div>
      </div>
    );
  }, [suggestedPrompts, activeSessionId, defaultEmptyMessage, handleSendMessage]);
  const defaultInputPlaceholder = inputPlaceholder || t('aiChat:inputPlaceholder');

  const isConnecting = session.status === 'connecting';

  return (
    <Sheet open={open} modal={false} onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}>
      <SheetContent
        side={panelSide}
        className={`${width} p-0 gap-0 flex flex-col overflow-hidden`}
        hideCloseButton
        hideOverlay
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <SheetTitle>{panelTitle}</SheetTitle>
        </VisuallyHidden>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <MessageSquare className="h-5 w-5 text-primary shrink-0" />
            <span className="font-semibold truncate">{panelTitle}</span>
          </div>
          <div className="flex items-center gap-1">
            {/* Voice/avatar call — only on deployments with LiveKit configured */}
            {avatarAvailability?.configured && !callActive && (
              <Button
                variant="ghost"
                size="icon"
                data-testid="chat-avatar-call"
                onClick={() => setCallActive(true)}
                className="h-8 w-8"
                aria-label={t('aiChat:call.start')}
                title={t('aiChat:call.start')}
              >
                <Video className="h-4 w-4" />
              </Button>
            )}
            {/* Session Selector */}
            {entitySessions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="chat-session-picker"
                    className="h-8 gap-1 max-w-[150px]"
                  >
                    <History className="h-4 w-4 shrink-0" />
                    <span className="truncate text-xs">{activeSessionDisplay}</span>
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" onCloseAutoFocus={handleDropdownCloseAutoFocus}>
                  <DropdownMenuItem data-testid="chat-new-session" onClick={handleNewChat}>
                    <Plus className="h-4 w-4" />
                    {t('aiChat:newChat')}
                  </DropdownMenuItem>
                  {entitySessions.length > 0 && <DropdownMenuSeparator />}
                  {entitySessions.map((s) => (
                    <DropdownMenuItem
                      key={s.id}
                      onClick={() => handleSwitchSession(s.id)}
                      className={s.id === activeSessionId ? 'bg-accent' : ''}
                    >
                      <div className="flex flex-col gap-0.5 w-full min-w-0">
                        <span className="truncate">{s.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {s.messageCount} {t('aiChat:messages')} · {formatSessionTime(s.updatedAt)}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* New Chat Button (when no sessions exist) */}
            {entitySessions.length === 0 && !isLoadingSessions && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="chat-new-session"
                onClick={handleNewChat}
                className="h-8"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
            {/* Close Button */}
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Extra header content (e.g., mode toggle) */}
        {headerExtra && (
          <div className="px-4 py-1.5 border-b flex items-center">
            {headerExtra}
          </div>
        )}

        {/* Entity badge (enhanced mode) */}
        {showEntityBadge && entityName && (
          <div className="px-4 py-1.5 border-b">
            <div className="px-2 py-1 bg-primary/10 border border-primary/20 rounded text-[10px] font-medium text-primary truncate">
              {entityName}
            </div>
          </div>
        )}

        {/* Attached-entities chip row (override mode, pre-first-message) */}
        {aboutEntitiesOverride && aboutEntitiesOverride.length > 0 && !activeSessionId && (
          <AttachedEntitiesChips
            entities={aboutEntitiesOverride}
            onRemove={
              onAboutEntitiesOverrideChange
                ? (ref) =>
                    onAboutEntitiesOverrideChange(
                      aboutEntitiesOverride.filter((e) => e.ref !== ref),
                    )
                : undefined
            }
          />
        )}

        {/* Chat Content */}
        <div className="flex-1 min-h-0 flex flex-col">
          {callActive ? (
            <AvatarCallView createSession={createAvatarCallSession} onEnd={handleCallEnd} />
          ) : isLoadingSessions || isConnecting ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ChatBox
              messages={displayMessages}
              onSend={handleSendMessage}
              onStop={machineStop}
              streaming={streaming}
              disabled={session.status === 'error'}
              height="100%"
              emptyMessage={defaultEmptyMessage}
              emptyStateContent={suggestedPromptsContent}
              inputPlaceholder={defaultInputPlaceholder}
              initialValue={pendingInitialInput}
              autoFocus={autoFocus && open}
              inputRef={chatInputRef}
              enabledModules={isEnhancedMode ? (mcpConfig?.enabledModules ?? []) : session.enabledModules}
              // Gates the debug inspector only — it renders a server-assembled
              // per-session prompt, so it stays session-bound.
              sessionId={isEnhancedMode ? (activeSessionId || undefined) : undefined}
              showMcpConfig={isEnhancedMode}
              mcpConfig={mcpConfig}
              onMcpConfigChange={isEnhancedMode ? handleMcpConfigChange : undefined}
              modelSelector={
                isEnhancedMode ? (
                  <ChatModelSelector
                    sessionModelCategory={sessionModelCategory}
                    modelName={modelName}
                    allowModelChoice={allowModelChoice}
                    onModelChange={handleModelChange}
                    sessionReasoningEffort={sessionReasoningEffort}
                    onReasoningEffortChange={handleReasoningEffortChange}
                  />
                ) : undefined
              }
            />
          )}
        </div>

        {/* Error Display */}
        {session.error && (
          <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm border-t">
            {session.error?.startsWith('chat.errors.')
              ? t(`aiChat:errors.${session.error.replace('chat.errors.', '')}`)
              : session.error}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Chip row showing entities that will be attached to the next-created chat
 * session. Renders up to MAX_VISIBLE chips inline, with "+N more" expandable
 * to a scrollable list. When onRemove is provided, each chip has an × button.
 */
function AttachedEntitiesChips({
  entities,
  onRemove,
}: {
  entities: Array<{ ref: string; label: string; type?: string }>;
  onRemove?: (ref: string) => void;
}) {
  const { t } = useTranslation('aiChat');
  const MAX_VISIBLE = 3;
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entities : entities.slice(0, MAX_VISIBLE);
  const overflow = entities.length - MAX_VISIBLE;

  return (
    <div className="px-4 py-2 border-b bg-muted/30">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground me-1">
          {t('attachedEntities.about', { defaultValue: 'About:' })}
        </span>
        {visible.map((e) => (
          <span
            key={e.ref}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs max-w-[180px]"
            title={e.label}
          >
            <span className="truncate">{e.label}</span>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(e.ref)}
                className="flex h-8 w-8 sm:h-4 sm:w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground shrink-0"
                aria-label={t('attachedEntities.remove', {
                  defaultValue: 'Remove',
                })}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {!expanded && overflow > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            {t('attachedEntities.more', {
              count: overflow,
              defaultValue: '+{{count}} more',
            })}
          </button>
        )}
      </div>
    </div>
  );
}
